package components.assemblyPortfolio

import components.gridAssessment.entryPreview
import it.neckar.commons.kotlin.js.getNotNull
import it.neckar.commons.kotlin.js.safeGet
import it.neckar.lizergy.model.assemblyPortfolio.AssemblyPortfolio
import it.neckar.lizergy.model.assemblyPortfolio.ResolvedAssemblyPortfolio
import it.neckar.lizergy.model.configuration.components.Einspeiseart
import it.neckar.lizergy.model.configuration.quote.QuoteConfiguration
import it.neckar.lizergy.model.energy.kiloWattPeak
import it.neckar.lizergy.model.energy.volts
import it.neckar.lizergy.model.financial.quote.ConfiguredOptionality
import it.neckar.lizergy.model.project.ResolvedProject
import it.neckar.open.kotlin.lang.nullIfBlank
import it.neckar.react.common.*
import it.neckar.react.common.form.*
import it.neckar.react.common.form.EditableStatus.Companion.toEditableStatus
import it.neckar.react.common.router.*
import plannerI18nConfiguration
import react.*
import react.dom.*
import react.router.dom.*
import router.RouterUrls

internal val BasisdatenOverview: FC<BasisdatenOverviewProps> = fc("BasisdatenOverview") { props ->
  val project = props::project.safeGet()
  val configuration = props::currentConfiguration.safeGet()
  val assemblyPortfolio = props::assemblyPortfolioToSave.getNotNull()
  val gridAssessment = project.gridAssessment

  val einspeiseart = useState(assemblyPortfolio.value.einspeiseart)
  val wartungsvertrag = useState(assemblyPortfolio.value.wartungsvertrag ?: (configuration?.facilityConfiguration?.onlineMonitoring == ConfiguredOptionality.Selected))
  val password = useState(assemblyPortfolio.value.anlagenPasswort)

  val pvLeistung = useState(assemblyPortfolio.value.pvLeistung?.kiloWattPeak)
  val leerlaufspannung = useState(assemblyPortfolio.value.leerlaufspannung?.volts)
  val messkonzept = useState(assemblyPortfolio.value.messkonzept)
  val messkonzeptOverride = useState(assemblyPortfolio.value.messkonzeptOverride)
  val ueberspannungsschutz = useState(assemblyPortfolio.value.ueberspannungsschutz)
  val ueberspannungsschutzAC = useState(assemblyPortfolio.value.ueberspannungsschutzAC)


  useEffect(
    einspeiseart.value,
    wartungsvertrag.value,
    password.value,
    pvLeistung.value,
    leerlaufspannung.value,
    messkonzept.value,
    messkonzeptOverride.value,
    ueberspannungsschutz.value,
    ueberspannungsschutzAC.value,
  ) {
    assemblyPortfolio.setter {
      it.copy(
        einspeiseart = if (einspeiseart.value != configuration?.einspeiseart) einspeiseart.value else null,
        wartungsvertrag = if (wartungsvertrag.value != (configuration?.facilityConfiguration?.onlineMonitoring == ConfiguredOptionality.Selected)) wartungsvertrag.value else null,
        anlagenPasswort = password.value,
        pvLeistung = pvLeistung.value?.kiloWattPeak,
        leerlaufspannung = leerlaufspannung.value?.volts,
        messkonzept = messkonzept.value,
        messkonzeptOverride = messkonzeptOverride.value.nullIfBlank(),
        ueberspannungsschutz = ueberspannungsschutz.value.nullIfBlank(),
        ueberspannungsschutzAC = ueberspannungsschutzAC.value.nullIfBlank(),
      )
    }
  }


  div("row my-4") {
    div("col-lg-6") {
      h3("my-3") { +"Basisdaten" }

      entryPreview("Verteiler Netzbetreiber") {
        linkToGridAssessment(project, gridAssessment.verteilerNetzbetreiberName ?: "-")
      }
      entryPreview("Wartungsvertrag") {
        checkbox(
          valueAndSetter = wartungsvertrag,
          fieldName = "wartungsvertrag",
          title = "Wartungsvertrag",
        )
      }
      div("my-2") {
        nullableFloatingInputField(
          valueAndSetter = password,
          fieldName = "password",
          title = "Anlagenpasswort",
        )
      }
      div("my-2") {
        floatingSelectNullable(
          valueAndSetter = einspeiseart,
          idProvider = { it?.name ?: "NONE" },
          formatter = { it?.name ?: "-" },
          fieldName = "einspeiseart",
          title = "Einspeiseart [${configuration?.einspeiseart?.format(plannerI18nConfiguration) ?: "-"}]",
          availableOptionsWithoutNull = Einspeiseart.entries,
        )
      }

      entryPreview("Installierte PV Leistung", "mb-0") {
        nullableFloatingDoubleInputField(
          valueAndSetter = pvLeistung,
          fieldName = "pvLeistung",
          title = configuration?.moduleLayouts?.totalPowerRating?.formatKiloWattPeak(plannerI18nConfiguration) ?: "-",
        ) {
          attrs {
            mergedBelow()
          }
        }
      }
      entryPreview("Leerlaufspannung [Uoc]", "mt-0") {
        nullableFloatingDoubleInputField(
          valueAndSetter = leerlaufspannung,
          fieldName = "leerlaufspannung",
          title = configuration?.moduleLayouts?.totalOffLoadVoltage?.format(plannerI18nConfiguration) ?: "-",
        ) {
          attrs {
            mergedAbove()
          }
        }
      }

      entryPreview("Messkonzept") {
        floatingSelectNullable(
          valueAndSetter = messkonzept,
          idProvider = { it ?: "NONE" },
          formatter = { it ?: "-" },
          fieldName = "messkonzept",
          title = "Messkonzept",
          availableOptionsWithoutNull = AssemblyPortfolio.messkonzepte,
          editableStatus = messkonzeptOverride.value?.isEmpty()?.toEditableStatus() ?: EditableStatus.Editable,
        ) {
          attrs {
            mergedBelow()
          }
        }
        nullableFloatingInputField(
          valueAndSetter = messkonzeptOverride,
          fieldName = "messkonzeptOverride",
          title = "Messkonzept",
        ) {
          attrs {
            mergedAbove()
          }
        }
      }
      entryPreview("ÜSS DC Typ") {
        nullableFloatingInputField(
          valueAndSetter = ueberspannungsschutz,
          fieldName = "ueberspannungsschutz",
          title = configuration?.ueberspannungsSchutzType ?: "-",
        )
      }
      entryPreview("ÜSS AC") {
        nullableFloatingInputField(
          valueAndSetter = ueberspannungsschutzAC,
          fieldName = "ueberspannungsschutzAC",
          title = configuration?.ueberspannungsschutzACType ?: "-",
        )
      }
      entryPreview("Einspeiseleistung Netz") {
        linkToGridAssessment(project, gridAssessment.einstellungWechselrichter.format())
      }
    }
  }
}

fun RBuilder.linkToGridAssessment(project: ResolvedProject, linkContent: String) {
  Link {
    attrs {
      toUrl = RouterUrls.project(project).gridAssessment
    }

    +linkContent
  }
}

external interface BasisdatenOverviewProps : Props {
  var project: ResolvedProject
  var currentConfiguration: QuoteConfiguration?
  var assemblyPortfolioToSave: StateInstance<ResolvedAssemblyPortfolio>
}
